/* JSON-LD CollectionPage + ItemList schema for cookbook pages.
 *
 * Each public cookbook becomes a topical hub Google can index:
 *   "Italian recipes" search → Nonna's Kitchen cookbook page surfaces
 *   with the recipe list inline as ItemList entries.
 *
 * Schema mix:
 *   - @type: CollectionPage (page-level wrapper) with mainEntity → ItemList
 *   - ItemList entries reference the canonical recipe URL + name
 *   - Each list entry is a lightweight ListItem (position + url + name);
 *     full Recipe schema lives on /r/{slug-id} pages.
 *
 * Privacy guard: only renders for cookbooks where `privacy === "public"`.
 *
 * Also emits <link rel="canonical"> and OG / Twitter meta — same pattern
 * as RecipeJsonLd so legacy /cb/cb_xxx URLs declare the SEO-slug canonical.
 */
import { useEffect } from "react";
import { cookbookPath, recipePath } from "@/lib/slug";

function buildCookbookSchema(cb, recipes, canonical, origin) {
  const publicRecipes = (recipes || []).filter((r) => r.is_public);

  const itemListElement = publicRecipes.slice(0, 200).map((r, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    url: `${origin}${recipePath(r)}`,
    name: r.title,
  }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: cb.title || "Recipe Filer cookbook",
    description: (cb.subtitle || "").slice(0, 500) ||
      `A collection of ${publicRecipes.length} recipe${publicRecipes.length === 1 ? "" : "s"} on Recipe Filer.`,
    url: canonical,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "Recipe Filer",
      url: "https://recipefiler.com",
    },
    author: cb.owner_name ? { "@type": "Person", name: cb.owner_name } : undefined,
    image: cb.cover_image || undefined,
    mainEntity: itemListElement.length
      ? {
          "@type": "ItemList",
          name: cb.title || "Recipes",
          numberOfItems: publicRecipes.length,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          itemListElement,
        }
      : undefined,
  };

  Object.keys(schema).forEach((k) => schema[k] === undefined && delete schema[k]);
  return schema;
}

export default function CookbookJsonLd({ cookbook, recipes }) {
  useEffect(() => {
    if (!cookbook || cookbook.privacy !== "public") return undefined;

    const origin = window.location.origin;
    const canonical = `${origin}${cookbookPath(cookbook)}`;
    const schema = buildCookbookSchema(cookbook, recipes, canonical, origin);

    const tagId = "rf-cookbook-jsonld";
    let script = document.getElementById(tagId);
    if (!script) {
      script = document.createElement("script");
      script.id = tagId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(schema);

    // BreadcrumbList — Home › Directory › {Cookbook}. Renders as the
    // clickable trail in Google search results.
    const breadcrumbId = "rf-cookbook-breadcrumb";
    let crumbScript = document.getElementById(breadcrumbId);
    if (!crumbScript) {
      crumbScript = document.createElement("script");
      crumbScript.id = breadcrumbId;
      crumbScript.type = "application/ld+json";
      document.head.appendChild(crumbScript);
    }
    const trail = [
      { name: "Recipe Filer", url: origin + "/" },
      { name: "Directory", url: origin + "/directory" },
      { name: cookbook.title || "Cookbook", url: canonical },
    ];
    crumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((t, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: t.name,
        item: t.url,
      })),
    });

    const canonicalId = "rf-cookbook-canonical";
    document.head.querySelectorAll('link[rel="canonical"]').forEach((el) => { if (el.id !== canonicalId) el.remove(); });
    let canonicalEl = document.getElementById(canonicalId);
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.id = canonicalId;
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = canonical;

    // OG + Twitter card — same pattern as RecipeJsonLd. Best-effort cover image.
    const heroImage = cookbook.cover_image || (recipes || []).find((r) => r.images?.[0])?.images?.[0] || "";
    const metaTags = [
      { property: "og:type", content: "website" },
      { property: "og:title", content: cookbook.title || "Recipe Filer cookbook" },
      { property: "og:description", content: (cookbook.subtitle || "").slice(0, 200) || `A collection of recipes on Recipe Filer.` },
      { property: "og:url", content: canonical },
      { property: "og:image", content: heroImage },
      { property: "og:site_name", content: "Recipe Filer" },
      { name: "twitter:card", content: heroImage ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: cookbook.title || "Recipe Filer cookbook" },
      { name: "twitter:description", content: (cookbook.subtitle || "").slice(0, 200) },
      { name: "twitter:image", content: heroImage },
    ];
    const addedMetas = [];
    metaTags.forEach((m) => {
      if (!m.content) return;
      const key = m.property || m.name;
      const same = document.head.querySelectorAll(m.property ? `meta[property="${key}"]` : `meta[name="${key}"]`);
      same.forEach((x, i) => { if (i > 0) x.remove(); });
      let el = same[0] || null;
      if (el) el.setAttribute("data-rf", "1");
      if (!el) {
        el = document.createElement("meta");
        if (m.property) el.setAttribute("property", m.property);
        if (m.name) el.setAttribute("name", m.name);
        el.setAttribute("data-rf", "1");
        document.head.appendChild(el);
        addedMetas.push(el);
      }
      el.setAttribute("content", m.content);
    });

    return () => {
      const s = document.getElementById(tagId);
      if (s) s.remove();
      const c = document.getElementById(canonicalId);
      if (c) c.remove();
      const cb = document.getElementById("rf-cookbook-breadcrumb");
      if (cb) cb.remove();
      addedMetas.forEach((el) => el.remove());
    };
  }, [cookbook, recipes]);

  return null;
}
